/**
 * Session-related localStorage keys and user-specific storage cleanup.
 *
 * Extracted from SessionProvider so the storage-key tables and the
 * collect-then-remove sweep are React-free and unit-testable. TOKEN_STORAGE_KEY
 * is imported back into SessionProvider (used ~12x); the prefix/key tables and
 * clearUserSpecificStorage are invoked on logout and onboarding-reset.
 */

import {
  EMAIL_FOLDER_CACHE_KEY,
  EMAIL_FOLDER_FETCHED_KEY,
  EMAIL_FOLDER_CACHE_PREFIX,
  EMAIL_FOLDER_FETCHED_PREFIX,
  EMAIL_INBOX_CACHE_PREFIX,
  EMAIL_DRAFTS_CACHE_PREFIX,
  EMAIL_OPTIMISTIC_OVERLAY_PREFIX,
  EMAIL_DELETED_DRAFT_IDS_PREFIX,
  ANALYSIS_CONTACT_CACHE_KEY,
  EMAIL_DISMISSED_SENDERS_KEY,
  EMAIL_HIDDEN_SENDERS_KEY,
  EMAIL_SENT_MARKER_PREFIX,
} from '@/lib/emailCacheKeys'
import { bumpEmailCacheEpoch } from '@/lib/emailCacheEpoch'

export const TOKEN_STORAGE_KEY = 'xavior_auth_token'
export const GUEST_ID_STORAGE_KEY = 'xavior_guest_id'

// User-specific localStorage keys that should be cleared on logout or account reset
const USER_SPECIFIC_STORAGE_KEYS = [
  'chat-user-name',
  'chat-assistant-name',
  'xavior_conversation_order',
  // Sender-level email preferences hold the prior user's sender addresses
  // (minor PII) and would otherwise be inherited by the next identity in the
  // same browser. Logout-only — they are same-user preferences shared across
  // that user's containers, so a container switch must NOT wipe them (which
  // is why they live here and not in the clearEmailCaches tables below).
  EMAIL_DISMISSED_SENDERS_KEY, // senderStrikes.ts
  EMAIL_HIDDEN_SENDERS_KEY, // modules/email/constants.HIDDEN_SENDERS_KEY
  // #5462: the Drive browser's indexed-folder cache. A single global key (not
  // per-account) that holds the prior identity's Drive folder structure, so it
  // must be cleared at the identity boundary. Literal (not imported) because
  // features/auth may not import modules/drive; kept in sync with
  // GoogleDriveBrowser.INDEXED_FOLDERS_KEY.
  'xavior:gdrive:indexed-folders:v1',
]

// Prefixes for user-specific keys whose suffix varies per workspace/contact
// and therefore cannot be enumerated up-front. Iterated and removed below.
const USER_SPECIFIC_STORAGE_PREFIXES = [
  // #2715: compose-draft stash. Keyed by workspace+contact; the 24h TTL caps
  // accidental cross-session exposure but logout should hard-clear too.
  'xavior:compose:draft:',
  // #2726: calendar-scope selector (keyed per Events module id) — cleared for
  // symmetry with other user-scoped UI state on account switch.
  'xavior:calendar-scope:',
  // #5257: durable chat email-draft "sent" markers (#4982). Logout-only —
  // like the sender preferences above they are per-user duplicate-send
  // guards, so a container switch must not wipe them.
  EMAIL_SENT_MARKER_PREFIX,
  // #5462: the Drive stats cache, keyed `drive-stats:v1:<accountId>`. The cached
  // payload includes the connected Google account's EMAIL address + file counts,
  // so the prior identity's Drive account must not survive logout on a shared
  // machine. Literal (not imported) because features/auth may not import
  // modules/drive; kept in sync with DriveStatsModal.STATS_LS_PREFIX.
  'drive-stats:v1:',
]

// Email caches that hold the previous identity's mail content. The
// sessionStorage entries survive both logout and the container-switch
// `window.location.reload()`, so useFolderMessages/useCachedMessages would
// re-seed and render the old identity's rows (#5276). The analysis
// contact-id cache lives in localStorage. Purged at both identity
// boundaries: logout (via clearUserSpecificStorage) and selectContainer.
//
// EMAIL_FOLDER_CACHE_KEY/EMAIL_FOLDER_FETCHED_KEY are the legacy GLOBAL
// (pre-#5557) keys — kept here defensively in case an instance never claimed
// (and thus never retired) that blob; the per-instance folder-cache/
// optimistic-overlay/deleted-draft-ids keys (#5557/#5558) are swept via the
// prefix list below, same as the inbox cache.
const EMAIL_CACHE_SESSION_KEYS = [EMAIL_FOLDER_CACHE_KEY, EMAIL_FOLDER_FETCHED_KEY]
const EMAIL_CACHE_SESSION_PREFIXES = [
  EMAIL_INBOX_CACHE_PREFIX,
  EMAIL_DRAFTS_CACHE_PREFIX,
  EMAIL_FOLDER_CACHE_PREFIX,
  EMAIL_FOLDER_FETCHED_PREFIX,
  EMAIL_OPTIMISTIC_OVERLAY_PREFIX,
  EMAIL_DELETED_DRAFT_IDS_PREFIX,
]
const EMAIL_CACHE_LOCAL_KEYS = [ANALYSIS_CONTACT_CACHE_KEY]

// #5565: the calendar connection/account-chrome restore cache
// (`useCalendarSync.ts`) — sessionStorage snapshots of `calendarConnected`
// and `calendarAccounts` (the latter holds PII: connected accounts' emails,
// provider, and sync-error text), keyed per calendar-module instance. Like
// the email caches above these entries survive both logout and the
// container-switch reload, so the prior identity's calendar accounts would
// otherwise leak into the next identity's first paint on the same tab.
// Swept via the same `clearEmailCaches` sessionStorage-prefix sweep so both
// identity boundaries (logout, container switch) are covered by one path —
// literal (not imported) because features/auth may not import
// modules/calendar; kept in sync with `useCalendarSync.ts`'s
// `calendarConnectedStorageKey`/`calendarAccountsStorageKey`.
const CALENDAR_CACHE_SESSION_PREFIXES = ['xavior:calendar-connected:', 'xavior:calendar-accounts:']

// Remove every key in `storage` that starts with one of `prefixes`.
// Collect first, then remove — mutating storage while iterating its indices
// via `key(i)` would shift remaining indices and skip entries.
function sweepPrefixes(storage: Storage, prefixes: string[]) {
  const prefixMatches: string[] = []
  for (let i = 0; i < storage.length; i++) {
    const k = storage.key(i)
    if (k && prefixes.some((p) => k.startsWith(p))) {
      prefixMatches.push(k)
    }
  }
  prefixMatches.forEach((k) => storage.removeItem(k))
}

export function clearEmailCaches() {
  if (typeof window === 'undefined') return
  EMAIL_CACHE_SESSION_KEYS.forEach((key) => {
    sessionStorage.removeItem(key)
  })
  sweepPrefixes(sessionStorage, EMAIL_CACHE_SESSION_PREFIXES)
  sweepPrefixes(sessionStorage, CALENDAR_CACHE_SESSION_PREFIXES)
  EMAIL_CACHE_LOCAL_KEYS.forEach((key) => {
    localStorage.removeItem(key)
  })
  // #5295: signal mounted email hooks that their in-memory caches predate
  // this purge — their persist callbacks become no-ops and the next fetch
  // re-seeds from scratch, so the reload:false container-sync path can't
  // re-persist the purged rows from surviving refs.
  bumpEmailCacheEpoch()
}

/**
 * Clear user-specific localStorage to prevent stale state
 * Called on logout and when server indicates onboarding not completed
 */
export function clearUserSpecificStorage() {
  if (typeof window === 'undefined') return
  USER_SPECIFIC_STORAGE_KEYS.forEach((key) => {
    localStorage.removeItem(key)
  })
  // Sweep prefix-matched keys (compose drafts and other multi-instance state).
  sweepPrefixes(localStorage, USER_SPECIFIC_STORAGE_PREFIXES)
  clearEmailCaches()
}
